<template>
  <div class="phase-control" :class="{ compact }">
    <template v-if="compact">
      <button
        type="button"
        class="phase-button"
        :class="{
          active: true,
          'opponent-turn': isOpponentTurn,
          'auto-phase': isAutoPhase(currentPhase),
        }"
      >
        {{ currentPhase }}
        <span v-if="!watch" class="current-player-indicator">
          {{ isOpponentTurn ? t('duel.opponentParen') : t('duel.youParen') }}
        </span>
      </button>
      <button
        v-if="nextPhase"
        type="button"
        class="phase-button next-phase"
        :class="{
          disabled: !canAdvanceToPhase(nextPhase),
          'opponent-turn': isOpponentTurn,
          holding: nextHolding,
        }"
        :title="t('duel.holdToEndTurn')"
        :aria-label="t('duel.holdToEndTurn')"
        @click="onNextClick($event)"
        @pointerdown="onNextPointerDown"
        @pointerup="onNextPointerUp"
        @pointercancel="onNextPointerUp"
        @pointerleave="onNextPointerUp"
        @contextmenu.prevent
      >
        <svg
          class="next-icon"
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M8 5l8 7-8 7" />
        </svg>
      </button>
    </template>
    <template v-else>
      <button
        v-for="phase in phases"
        :key="phase"
        type="button"
        :class="{
          'phase-button': true,
          'active': currentPhase === phase,
          'disabled': currentPhase !== phase && !canAdvanceToPhase(phase),
          'opponent-turn': isOpponentTurn,
          'auto-phase': isAutoPhase(phase),
          'current-player': isOpponentTurn ? 'OPPONENT' : 'PLAYER'
        }"
        @click="handlePhaseClick(phase, $event)"
      >
        {{ phase }}
        <span v-if="currentPhase === phase && !watch" class="current-player-indicator">
          {{ isOpponentTurn ? t('duel.opponentParen') : t('duel.youParen') }}
        </span>
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue';
import { Phase, Player } from '../types';
import { useI18n } from '../i18n/t'

const { t } = useI18n()

const props = defineProps<{
  gameState: any;
  /** Bumps when phase/turn/playback change so the HUD cannot stay stale. */
  revision?: string;
  compact?: boolean;
  /** Admin watch: show the phase, never advance it. */
  watch?: boolean;
}>();

const currentPhase = computed(() => {
  void props.revision
  const phase = props.gameState.getCurrentPhase();
  
  return phase;
});

const currentPlayer = computed(() => {
  void props.revision
  const player = props.gameState.getCurrentPlayer();
  
  return player;
});

const turnCount = computed(() => {
  void props.revision
  const turn = props.gameState.getTurnCount();
  
  return turn;
});

const isOpponentTurn = computed(() => {
  const isOpponent = currentPlayer.value === Player.PLAYER_2;
  
  return isOpponent;
});

const phases = Object.values(Phase);

const isAutoPhase = (phase: Phase): boolean => {
  // Draw is always skipped. Standby is shown, then the host advances to M1
  // after a short hold (clicking M1 still skips the wait).
  return phase === Phase.DRAW || phase === Phase.STANDBY;
};

const skipsBattle = computed(() => {
  void props.revision
  return !!props.gameState.skipsBattlePhase?.()
})

const noBattleThisTurn = computed(
  () => (turnCount.value === 1 && !isOpponentTurn.value) || skipsBattle.value,
)

const nextPhase = computed((): Phase | null => {
  const cur = currentPhase.value;
  if (cur === Phase.STANDBY) return Phase.MAIN_1;
  if (cur === Phase.MAIN_1) return noBattleThisTurn.value ? Phase.END : Phase.BATTLE;
  if (cur === Phase.BATTLE) return Phase.MAIN_2;
  if (cur === Phase.MAIN_2) return Phase.END;
  // Already in End Phase: the same button passes the turn.
  if (cur === Phase.END) return isOpponentTurn.value ? null : Phase.END;
  return null;
});

const canAdvanceToPhase = (phase: Phase): boolean => {
  if (props.watch) return false;
  if (isOpponentTurn.value) {
    return false;
  }

  if (props.gameState.isBattleInProgress?.()) {
    return false;
  }

  if (props.gameState.isGameEnded?.()) {
    return false;
  }

  if (isAutoPhase(phase)) return false;

  const cur = currentPhase.value;

  // Valid transitions:
  //   SP → M1
  //   M1 → BP (not on first turn, and not when Battle is skipped), M1 → EP
  //   BP → M2, BP → EP (skips Main 2)
  //   M2 → EP
  //   EP → EP (pass the turn; the button is the current phase)
  if (cur === Phase.STANDBY) {
    return phase === Phase.MAIN_1;
  }
  if (cur === Phase.MAIN_1) {
    if (phase === Phase.BATTLE) return !noBattleThisTurn.value;
    if (phase === Phase.END) return true;
    return false;
  }
  if (cur === Phase.BATTLE) {
    return phase === Phase.MAIN_2 || phase === Phase.END;
  }
  if (cur === Phase.MAIN_2) {
    return phase === Phase.END;
  }
  if (cur === Phase.END) {
    return phase === Phase.END;
  }

  return false;
};

const HOLD_MS = 400
const holdTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const nextHolding = ref(false)
const skipNextClick = ref(false)

const clearNextHold = (): void => {
  if (holdTimer.value) {
    clearTimeout(holdTimer.value)
    holdTimer.value = null
  }
  nextHolding.value = false
}

const onNextPointerDown = (): void => {
  if (!canAdvanceToPhase(Phase.END)) return
  skipNextClick.value = false
  nextHolding.value = true
  holdTimer.value = setTimeout(() => {
    holdTimer.value = null
    nextHolding.value = false
    skipNextClick.value = true
    void handlePhaseClick(Phase.END)
  }, HOLD_MS)
}

const onNextPointerUp = (): void => {
  clearNextHold()
}

const onNextClick = async (event: MouseEvent): Promise<void> => {
  if (skipNextClick.value) {
    skipNextClick.value = false
    return
  }
  if (!nextPhase.value) return
  await handlePhaseClick(nextPhase.value, event)
}

onUnmounted(() => clearNextHold())

/** Second click of a double-click. On the compact arrow the same button becomes
 *  the following phase as soon as the strip moves, so the extra click used to
 *  advance one phase further. */
const PHASE_CLICK_GAP_MS = 450
let lastPhaseAdvanceAt = 0
let lastPhaseTarget: Phase | null = null

const handlePhaseClick = async (phase: Phase, event?: MouseEvent) => {
  if (event && event.detail > 1) return
  const now = performance.now()
  const tooSoon = now - lastPhaseAdvanceAt < PHASE_CLICK_GAP_MS
  if (tooSoon && (props.compact || phase === lastPhaseTarget)) return
  if (!isOpponentTurn.value && canAdvanceToPhase(phase)) {
    lastPhaseAdvanceAt = now
    lastPhaseTarget = phase
    await props.gameState.setPhase(phase);
  }
};
</script>

<style scoped>
.phase-control {
  display: flex;
  gap: 8px;
  justify-content: center;
  padding: 8px;
  background: rgba(8, 10, 18, 0.72);
  border: 1px solid rgba(232, 197, 71, 0.22);
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.35);
}

.phase-button {
  position: relative;
  padding: 8px 12px;
  min-width: 74px;
  border: 1px solid rgba(232, 197, 71, 0.42);
  background: rgba(12, 14, 24, 0.88);
  color: #fde68a;
  border-radius: 8px;
  cursor: pointer;
  font-family: Cinzel, Palatino, serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    color 0.2s ease;
}

.phase-button:hover:not(.disabled):not(.auto-phase) {
  border-color: #e8c547;
  background: rgba(232, 197, 71, 0.14);
  box-shadow: 0 0 14px rgba(232, 197, 71, 0.28);
}

.phase-button:active:not(.disabled):not(.auto-phase) {
  scale: 0.94;
  filter: brightness(1.18);
}

.phase-button.active {
  background: linear-gradient(180deg, #fff3b0, #e8c547);
  color: #1a1204;
  border-color: #f5d76e;
  box-shadow: 0 0 16px rgba(232, 197, 71, 0.45);
}

.phase-button.opponent-turn {
  border-color: rgba(248, 113, 113, 0.55);
  color: #fecaca;
}

.phase-button.opponent-turn:hover:not(.disabled):not(.auto-phase) {
  border-color: #f87171;
  background: rgba(248, 113, 113, 0.14);
  box-shadow: 0 0 14px rgba(248, 113, 113, 0.28);
}

.phase-button.opponent-turn.active {
  background: linear-gradient(180deg, #fecaca, #f87171);
  color: #2a0a0a;
  border-color: #fca5a5;
  box-shadow: 0 0 16px rgba(248, 113, 113, 0.4);
}

.phase-button.auto-phase {
  cursor: default;
  opacity: 0.78;
  font-style: italic;
}

.phase-button.auto-phase:hover:not(.active) {
  background: rgba(12, 14, 24, 0.88);
  box-shadow: none;
}

.phase-button.auto-phase.opponent-turn:hover:not(.active) {
  background: rgba(12, 14, 24, 0.88);
  box-shadow: none;
}

.phase-button.disabled {
  opacity: 0.42;
  cursor: not-allowed;
  border-color: rgba(148, 163, 184, 0.28);
  color: #94a3b8;
}

.phase-button.disabled:hover {
  background: rgba(12, 14, 24, 0.88);
  box-shadow: none;
}

.phase-button.active.disabled,
.phase-button.active.auto-phase {
  background: linear-gradient(180deg, #fff3b0, #e8c547);
  color: #1a1204;
  border-color: #f5d76e;
  box-shadow: 0 0 16px rgba(232, 197, 71, 0.45);
  opacity: 1;
  cursor: default;
}

.phase-button.opponent-turn.active.disabled,
.phase-button.opponent-turn.active.auto-phase {
  background: linear-gradient(180deg, #fecaca, #f87171);
  color: #2a0a0a;
  border-color: #fca5a5;
  box-shadow: 0 0 16px rgba(248, 113, 113, 0.4);
}

.current-player-indicator {
  position: absolute;
  bottom: -18px;
  left: 50%;
  transform: translateX(-50%);
  font-family: Roboto, system-ui, sans-serif;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: none;
  white-space: nowrap;
  color: #fde68a;
  opacity: 0.9;
}

.phase-button.opponent-turn .current-player-indicator {
  color: #fecaca;
}

.phase-control.compact {
  gap: 6px;
  padding: 6px;
}

.phase-control.compact .phase-button {
  min-width: 44px;
  padding: 6px 8px;
  font-size: 12px;
}

.phase-control.compact .current-player-indicator {
  bottom: -16px;
  font-size: 0.7em;
}

.phase-control.compact .next-icon {
  display: block;
}

.phase-control.compact .next-phase {
  overflow: hidden;
  touch-action: none;
  user-select: none;
}

.phase-control.compact .next-phase.holding::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.28);
  transform-origin: left center;
  animation: next-hold 400ms linear forwards;
  pointer-events: none;
}

@keyframes next-hold {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}
</style> 